import { defineStore } from "pinia";

/**
 * Sidebar Count Store
 *
 * IMPORTANT: These counts represent TOTAL active counts and should be
 * INDEPENDENT of any filters applied to employee/position/role/responsibility lists.
 * The counts are updated via the /active-counts/ API endpoint which should
 * always return unfiltered totals.
 */
export const useSidebarCountStore = defineStore("sidebarCount", {
  state: () => ({
    employees: 0,
    employeesWithNoAccess: 0,
    employeesWithoutPosition: 0,
    positions: 0,
    roles: 0,
    responsibilities: 0,
    procedures: 0,
    departments: 0,
    draftEmployees: 0,
    draftPositions: 0,
    draftRoles: 0,
    draftResponsibilities: 0,
    draftDepartments: 0,
    // ER-12439 — account-wide gap counts, parsed from the same active-counts
    // payload so the Departments header dashboard reuses the store instead of
    // firing a duplicate request.
    orphanPositions: 0,
    departmentsNotManaged: 0,
    // True once an active-counts payload has been stored for the current
    // account/session. Lets a page decide whether it needs its own first load.
    accountCountsLoaded: false,
  }),

  getters: {
    getEmployeesCount: (state) => state.employees,
    getEmployeesWithNoAccessCount: (state) => state.employeesWithNoAccess,
    getEmployeesWithoutPositionCount: (state) => state.employeesWithoutPosition,
    getPositionsCount: (state) => state.positions,
    getRolesCount: (state) => state.roles,
    getResponsibilitiesCount: (state) => state.responsibilities,
    getProceduresCount: (state) => state.procedures,
    getDepartmentsCount: (state) => state.departments,
    getEmployeesDraftCount: (state) => state.draftEmployees,
    getPositionsDraftCount: (state) => state.draftPositions,
    getRolesDraftCount: (state) => state.draftRoles,
    getResponsibilitiesDraftCount: (state) => state.draftResponsibilities,
    getDepartmentsDraftCount: (state) => state.draftDepartments,
    getOrphanPositionsCount: (state) => state.orphanPositions,
    getDepartmentsNotManagedCount: (state) => state.departmentsNotManaged,
    hasAccountCounts: (state) => state.accountCountsLoaded,
    getAllCounts: (state) => ({
      employees: state.employees,
      employeesWithNoAccess: state.employeesWithNoAccess,
      employeesWithoutPosition: state.employeesWithoutPosition,
      positions: state.positions,
      roles: state.roles,
      responsibilities: state.responsibilities,
      procedures: state.procedures,
      departments: state.departments,
    }),
  },

  actions: {
    /**
     * Sets account counts from API response.
     * These counts should be independent of any filters.
     * @param {Object} counts - Object with employees, positions, roles, responsibilities, procedures counts
     */
    setAccountCounts(counts) {
      if (!counts || typeof counts !== "object") return;
      this.employees = counts.employees || 0;
      this.employeesWithNoAccess =
        counts["employees-with-no-access"] ?? counts.employeesWithNoAccess ?? 0;
      this.employeesWithoutPosition =
        counts["employees_without_position"] ?? counts.employeesWithoutPosition ?? 0;
      this.positions = counts.positions || 0;
      this.roles = counts.roles || 0;
      this.responsibilities = counts.responsibilities || 0;
      this.procedures = counts.procedures || 0;
      this.departments = counts.departments || 0;

      // ER-12265 / ER-12262 / ER-12264 — the v2 active-counts response carries a
      // `draft` object when the draft-count flags are on. Absent -> all draft
      // totals read 0 (the tiles then render "No drafts") without erroring.
      const draft = counts.draft && typeof counts.draft === "object" ? counts.draft : {};
      this.draftEmployees = draft.employees || 0;
      this.draftPositions = draft.positions || 0;
      this.draftRoles = draft.roles || 0;
      this.draftResponsibilities = draft.responsibilities || 0;
      this.draftDepartments = draft.departments || 0;

      // ER-12439 — the v2 payload also carries the account-wide gap counts the
      // Departments header dashboard shows. Missing (flag off server-side) -> 0.
      this.orphanPositions = counts.orphan_positions || 0;
      this.departmentsNotManaged = counts.departments_not_managed || 0;

      // The store now holds a real payload; consumers can read it instead of
      // issuing their own active-counts request.
      this.accountCountsLoaded = true;
    },

    resetCounts() {
      this.employees = 0;
      this.employeesWithNoAccess = 0;
      this.employeesWithoutPosition = 0;
      this.positions = 0;
      this.roles = 0;
      this.responsibilities = 0;
      this.procedures = 0;
      this.departments = 0;
      this.draftEmployees = 0;
      this.draftPositions = 0;
      this.draftRoles = 0;
      this.draftResponsibilities = 0;
      this.draftDepartments = 0;
      this.orphanPositions = 0;
      this.departmentsNotManaged = 0;
      this.accountCountsLoaded = false;
    },
  },
});
